import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  Tabs,
  Tab,
  Autocomplete,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Settings as AttributesIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  ColorLens as ColorIcon,
  Straighten as SizeIcon,
  Scale as WeightIcon,
  Inventory as StockIcon,
} from "@mui/icons-material";

function ProductAttributes() {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [attributeType, setAttributeType] = useState("all");
  const [attributes, setAttributes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    type: "text",
    values: [],
    category: "All Products",
    required: false,
    active: true,
    description: "",
  });

  const [attributeTypes, setAttributeTypes] = useState([
    { value: "text", label: "Text", icon: <LabelIcon /> },
    { value: "number", label: "Number", icon: <WeightIcon /> },
    { value: "color", label: "Color", icon: <ColorIcon /> },
    { value: "size", label: "Size", icon: <SizeIcon /> },
    { value: "date", label: "Date", icon: <LabelIcon /> },
    { value: "boolean", label: "Boolean", icon: <LabelIcon /> },
  ]);

  // Fetch attributes from API
  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/attributes`);
        console.log("Raw API response:", response);
        console.log("Response data:", response.data);

        // Handle MongoDB response format
        let attributesArray = [];
        if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          attributesArray = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          attributesArray = response.data;
        } else if (response.data && typeof response.data === "object") {
          // If it's an object, try to extract array from it
          const values = Object.values(response.data);
          attributesArray = values.find(Array.isArray) || [];
        }

        console.log("Setting attributes to:", attributesArray);
        setAttributes(attributesArray);

        // Always show all attribute types for complete selection
        const allTypes = [
          { value: "text", label: "Text", icon: <LabelIcon /> },
          { value: "number", label: "Number", icon: <WeightIcon /> },
          { value: "color", label: "Color", icon: <ColorIcon /> },
          { value: "size", label: "Size", icon: <SizeIcon /> },
          { value: "date", label: "Date", icon: <LabelIcon /> },
          { value: "boolean", label: "Boolean", icon: <LabelIcon /> },
        ];

        setAttributeTypes(allTypes);
        console.log("Generated attribute types:", allTypes);
      } catch (err) {
        console.error("Error fetching attributes:", err);
        setError("Failed to load attributes");
        setAttributes([]); // Ensure it's always an array

        // Set fallback attribute types
        const fallbackTypes = [
          { value: "text", label: "Text", icon: <LabelIcon /> },
          { value: "number", label: "Number", icon: <WeightIcon /> },
          { value: "color", label: "Color", icon: <ColorIcon /> },
          { value: "size", label: "Size", icon: <SizeIcon /> },
          { value: "date", label: "Date", icon: <LabelIcon /> },
          { value: "boolean", label: "Boolean", icon: <LabelIcon /> },
        ];
        setAttributeTypes(fallbackTypes);
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/categories`);
        console.log("Categories API response:", response);

        let categoriesArray = [];
        if (response.data && response.data.categories) {
          categoriesArray = response.data.categories;
        } else if (response.data && Array.isArray(response.data)) {
          categoriesArray = response.data;
        } else if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          categoriesArray = response.data.data;
        }

        // Add "All Products" option at the beginning
        const allCategories = [
          "All Products",
          ...categoriesArray.map((cat) => cat.name),
        ];
        console.log("Setting categories to:", allCategories);
        setCategories(allCategories);
      } catch (err) {
        console.error("Error fetching categories:", err);
        // Fallback to basic categories if API fails
        setCategories([
          "All Products",
          "Clothing",
          "Electronics",
          "Food",
          "Books",
        ]);
      }
    };

    fetchAttributes();
    fetchCategories();
  }, []);

  // Sync formData.category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !categories.includes(formData.category)) {
      setFormData((prev) => ({ ...prev, category: categories[0] }));
    }
  }, [categories, formData.category]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTypeFilterChange = (event) => {
    setAttributeType(event.target.value);
  };

  const filteredAttributes = Array.isArray(attributes)
    ? attributes.filter((attribute) => {
        const matchesSearch =
          attribute.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (attribute.description || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (attribute.category || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

        const matchesType =
          attributeType === "all" || attribute.type === attributeType;

        // Show all attributes (remove hardcoded filter)
        return matchesSearch && matchesType;
      })
    : [];

  const handleViewDetails = (attribute) => {
    setSelectedAttribute(attribute);
    setDetailsDialogOpen(true);
  };

  const handleEdit = (attribute) => {
    setFormData({
      name: attribute.name,
      type: attribute.type,
      values: attribute.values,
      category: attribute.category,
      required: attribute.required,
      active: attribute.active,
      description: attribute.description,
    });
    setSelectedAttribute(attribute);
    setEditDialogOpen(true);
  };

  const handleDelete = (attribute) => {
    setSelectedAttribute(attribute);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/attributes/${selectedAttribute._id}`);
      setAttributes(
        Array.isArray(attributes)
          ? attributes.filter((attr) => attr._id !== selectedAttribute._id)
          : [],
      );
      setDeleteDialogOpen(false);
      setSelectedAttribute(null);
      alert("Attribute deleted successfully!");
    } catch (error) {
      console.error("Error deleting attribute:", error);
      alert("Failed to delete attribute");
    }
  };

  const toggleActiveStatus = async (id) => {
    try {
      const attribute = attributes.find((attr) => attr._id === id);
      console.log(
        "🔄 Toggling attribute:",
        attribute.name,
        "from",
        attribute.active,
        "to",
        !attribute.active,
      );

      const response = await axios.put(`${API_BASE_URL}/attributes/${id}`, {
        ...attribute,
        active: !attribute.active,
      });

      console.log("✅ Toggle response:", response.data);

      setAttributes(
        Array.isArray(attributes)
          ? attributes.map((attr) =>
              attr._id === id ? { ...attr, active: !attr.active } : attr,
            )
          : [],
      );

      console.log("🔄 Local state updated");
    } catch (error) {
      console.error("❌ Error toggling attribute status:", error);
      console.error("❌ Error response:", error.response?.data);
      alert("Failed to update attribute status");
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      type: "text",
      values: [],
      category: "All Products",
      required: false,
      active: true,
      description: "",
    });
    setSelectedAttribute(null);
  };

  const handleSaveAttribute = async () => {
    try {
      const newAttribute = {
        name: formData.name,
        type: formData.type,
        values: formData.values,
        category: formData.category,
        required: formData.required,
        active: formData.active,
        description: formData.description,
      };

      console.log("🔍 Sending attribute data:", newAttribute);

      let response;
      if (selectedAttribute) {
        // Update existing attribute
        console.log("🔄 Updating attribute with ID:", selectedAttribute._id);
        response = await axios.put(
          `${API_BASE_URL}/attributes/${selectedAttribute._id}`,
          newAttribute,
        );
        console.log("✅ Attribute updated response:", response.data);

        // Update attributes in state
        setAttributes(
          attributes.map((attr) =>
            attr._id === selectedAttribute._id ? response.data : attr,
          ),
        );
        alert("Attribute updated successfully!");
      } else {
        // Create new attribute
        console.log("➕ Creating new attribute");
        response = await axios.post(`${API_BASE_URL}/attributes`, newAttribute);
        console.log("✅ Attribute added response:", response.data);

        // Add new attribute to state
        setAttributes([...attributes, response.data]);
        alert("Attribute added successfully!");
      }

      // Reset form
      setFormData({
        name: "",
        type: "text",
        values: [],
        category: "All Products",
        required: false,
        active: true,
        description: "",
      });
      setEditDialogOpen(false);
      setSelectedAttribute(null);
    } catch (error) {
      console.error("❌ Error saving attribute:", error);
      console.error("❌ Error response:", error.response?.data);
      alert(
        `Failed to save attribute: ${error.response?.data?.error || error.message}`,
      );
    }
  };

  const handleExport = () => {
    const csvContent = [
      [
        "ID",
        "Name",
        "Type",
        "Values",
        "Category",
        "Required",
        "Active",
        "Created At",
        "Description",
      ],
      ...filteredAttributes.map((attr) => [
        attr.id,
        attr.name,
        attr.type,
        attr.values.join("; "),
        attr.category,
        attr.required ? "Yes" : "No",
        attr.active ? "Yes" : "No",
        attr.createdAt,
        attr.description,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "product-attributes.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "color":
        return <ColorIcon />;
      case "size":
        return <SizeIcon />;
      case "number":
        return <WeightIcon />;
      default:
        return <LabelIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "color":
        return "#9c27b0";
      case "size":
        return "#ff9800";
      case "number":
        return "#2196f3";
      default:
        return "#4caf50";
    }
  };

  const getTypeBgColor = (type) => {
    switch (type) {
      case "color":
        return "#f3e5f5";
      case "size":
        return "#fff3e0";
      case "number":
        return "#e3f2fd";
      default:
        return "#e8f5e8";
    }
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "#f3f4f6", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
          Product Attributes
        </Typography>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Typography>Loading attributes...</Typography>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Language Tabs */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 3,
              backgroundColor: "white",
            }}
          >
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="English (EN)" />
            </Tabs>
          </Box>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Attribute Setup Form */}
            <Grid item xs={12} lg={4}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e6e6e6",
                  backgroundColor: "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    transform: "translateY(-2px)",
                  },
                  height: "fit-content",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#333" }}
                  >
                    {selectedAttribute ? "Edit Attribute" : "Add New Attribute"}
                  </Typography>

                  {/* Attribute Name */}
                  <TextField
                    fullWidth
                    label="Attribute Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fafafa",
                      },
                    }}
                  />

                  {/* Attribute Type */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Attribute Type</InputLabel>
                    <Select
                      value={
                        attributeTypes.find((t) => t.value === formData.type)
                          ? formData.type
                          : ""
                      }
                      onChange={(e) => {
                        console.log("Type selected:", e.target.value);
                        setFormData({ ...formData, type: e.target.value });
                      }}
                      label="Attribute Type"
                      size="small"
                    >
                      {console.log("Available attributeTypes:", attributeTypes)}
                      {attributeTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {type.icon}
                            {type.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Category */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={
                        categories.includes(formData.category)
                          ? formData.category
                          : ""
                      }
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      label="Category"
                      size="small"
                    >
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Values */}
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.values}
                    onChange={(e, newValue) =>
                      setFormData({ ...formData, values: newValue })
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          size="small"
                          sx={{
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            borderRadius: 1,
                            fontSize: "12px",
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Values (press Enter to add)"
                        variant="outlined"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#fafafa",
                          },
                        }}
                      />
                    )}
                  />

                  <Divider sx={{ my: 2 }} />

                  {/* Description */}
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fafafa",
                      },
                    }}
                  />

                  <Divider sx={{ my: 2 }} />

                  {/* Required & Active */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">Required:</Typography>
                      <Switch
                        checked={formData.required}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            required: e.target.checked,
                          })
                        }
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">Active:</Typography>
                      <Switch
                        checked={formData.active}
                        onChange={(e) =>
                          setFormData({ ...formData, active: e.target.checked })
                        }
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Form Buttons */}
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      size="medium"
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        borderColor: "#ddd",
                        color: "#666",
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveAttribute}
                      size="medium"
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        backgroundColor: "#1976d2",
                      }}
                    >
                      {selectedAttribute ? "Update Attribute" : "Add Attribute"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Attributes List */}
            <Grid item xs={12} lg={8}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e6e6e6",
                  backgroundColor: "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 2, sm: 0 },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#333" }}
                    >
                      Attributes List ({filteredAttributes.length})
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        flexDirection: { xs: "column", sm: "row" },
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <TextField
                        placeholder="Search attributes..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <SearchIcon
                              sx={{ mr: 1, color: "#666", fontSize: 20 }}
                            />
                          ),
                        }}
                        sx={{ minWidth: { xs: "100%", sm: 250 } }}
                      />
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={attributeType}
                          onChange={handleTypeFilterChange}
                          label="Type"
                        >
                          <MenuItem value="all">All Types</MenuItem>
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="number">Number</MenuItem>
                          <MenuItem value="color">Color</MenuItem>
                          <MenuItem value="size">Size</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        onClick={handleExport}
                        size="small"
                        sx={{ borderColor: "#ddd", color: "#666" }}
                      >
                        Export
                      </Button>
                    </Box>
                  </Box>

                  {/* Attributes Table - Responsive */}
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{ border: "1px solid #e0e0e0" }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: "#fafafa" }}>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              ID
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              Name
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              Type
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              Values
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              Category
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              Required
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              Active
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredAttributes.map((attribute) => (
                            <TableRow
                              key={attribute._id}
                              hover
                              sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                            >
                              <TableCell
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                {attribute._id}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderBottom: "1px solid #e0e0e0",
                                  color: "#333",
                                  fontWeight: 500,
                                }}
                              >
                                {attribute.name}
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {getTypeIcon(attribute.type)}
                                  <Chip
                                    label={
                                      attribute.type.charAt(0).toUpperCase() +
                                      attribute.type.slice(1)
                                    }
                                    size="small"
                                    sx={{
                                      backgroundColor: getTypeBgColor(
                                        attribute.type,
                                      ),
                                      color: getTypeColor(attribute.type),
                                      borderColor: getTypeColor(attribute.type),
                                      fontWeight: 500,
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {(attribute.values || attribute.options || [])
                                    .slice(0, 3)
                                    .map((value, index) => (
                                      <Chip
                                        key={index}
                                        label={value}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          fontSize: "11px",
                                          height: "20px",
                                        }}
                                      />
                                    ))}
                                  {(attribute.values || attribute.options || [])
                                    .length > 3 && (
                                    <Chip
                                      label={`+${(attribute.values || attribute.options || []).length - 3}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: "11px",
                                        height: "20px",
                                        color: "#666",
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Chip
                                  label={attribute.category || "General"}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: "#f5f5f5",
                                    color: "#666",
                                    borderColor: "#ddd",
                                    fontSize: "11px",
                                    height: "20px",
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Chip
                                  label={attribute.required ? "Yes" : "No"}
                                  size="small"
                                  color={
                                    attribute.required ? "success" : "default"
                                  }
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: attribute.required
                                      ? "#e8f5e8"
                                      : "#f5f5f5",
                                    borderColor: attribute.required
                                      ? "#4caf50"
                                      : "#ccc",
                                    color: attribute.required
                                      ? "#4caf50"
                                      : "#666",
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Switch
                                  checked={attribute.active}
                                  onChange={() =>
                                    toggleActiveStatus(attribute._id)
                                  }
                                  color="primary"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#1976d2" }}
                                    onClick={() => handleViewDetails(attribute)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#4caf50" }}
                                    onClick={() => handleEdit(attribute)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#f44336" }}
                                    onClick={() => handleDelete(attribute)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Mobile Card View */}
                  <Box sx={{ display: { xs: "block", md: "none" } }}>
                    <Grid container spacing={2}>
                      {filteredAttributes.map((attribute) => (
                        <Grid item xs={12} key={attribute._id}>
                          <Card
                            sx={{
                              borderRadius: 3,
                              border: "1px solid #e6e6e6",
                              backgroundColor: "#fff",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 600, color: "#333" }}
                                >
                                  {attribute.name}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#1976d2" }}
                                    onClick={() => handleViewDetails(attribute)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#4caf50" }}
                                    onClick={() => handleEdit(attribute)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#f44336" }}
                                    onClick={() => handleDelete(attribute)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 2,
                                }}
                              >
                                {getTypeIcon(attribute.type)}
                                <Chip
                                  label={
                                    attribute.type.charAt(0).toUpperCase() +
                                    attribute.type.slice(1)
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: getTypeBgColor(
                                      attribute.type,
                                    ),
                                    color: getTypeColor(attribute.type),
                                    borderColor: getTypeColor(attribute.type),
                                    fontWeight: 500,
                                  }}
                                />
                                <Chip
                                  label={attribute.category || "General"}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: "#1976d2",
                                    color: "#1976d2",
                                    fontWeight: 500,
                                  }}
                                />
                              </Box>

                              {(attribute.values || attribute.options || [])
                                .length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#666",
                                      fontWeight: 500,
                                      mb: 1,
                                    }}
                                  >
                                    Values:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {(
                                      attribute.values ||
                                      attribute.options ||
                                      []
                                    ).map((value, index) => (
                                      <Chip
                                        key={index}
                                        label={value}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          backgroundColor: "#f8f9fa",
                                          borderColor: "#dee2e6",
                                          color: "#495057",
                                          fontWeight: 500,
                                          fontSize: "11px",
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Chip
                                    label={
                                      attribute.required
                                        ? "Required"
                                        : "Optional"
                                    }
                                    size="small"
                                    color={
                                      attribute.required ? "success" : "default"
                                    }
                                    sx={{
                                      backgroundColor: attribute.required
                                        ? "#e8f5e8"
                                        : "#f5f5f5",
                                      borderColor: attribute.required
                                        ? "#4caf50"
                                        : "#ccc",
                                      color: attribute.required
                                        ? "#4caf50"
                                        : "#666",
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Chip
                                    label={
                                      attribute.active ? "Active" : "Inactive"
                                    }
                                    size="small"
                                    color={
                                      attribute.active ? "primary" : "default"
                                    }
                                    sx={{
                                      backgroundColor: attribute.active
                                        ? "#e3f2fd"
                                        : "#f5f5f5",
                                      borderColor: attribute.active
                                        ? "#1976d2"
                                        : "#ccc",
                                      color: attribute.active
                                        ? "#1976d2"
                                        : "#666",
                                      fontWeight: 600,
                                    }}
                                  />
                                </Box>
                                <Switch
                                  checked={attribute.active}
                                  onChange={() =>
                                    toggleActiveStatus(attribute._id)
                                  }
                                  color="primary"
                                  size="small"
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Details Dialog */}
          <Dialog
            open={detailsDialogOpen}
            onClose={() => setDetailsDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: 3,
                m: { xs: 1, md: 2 },
              },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "primary.main",
                color: "white",
                py: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {getTypeIcon(selectedAttribute?.type)}
                <Typography variant="h6" component="div">
                  Attribute Details
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {selectedAttribute && (
                <Box>
                  {/* Basic Information Card */}
                  <Card
                    sx={{
                      mb: 3,
                      borderRadius: 3,
                      border: "1px solid #e6e6e6",
                      backgroundColor: "#fff",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 3, color: "#333", fontWeight: 600 }}
                      >
                        Basic Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontWeight: 500, mb: 0.5 }}
                            >
                              Attribute ID
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                bgcolor: "#f5f5f5",
                                p: 1,
                                borderRadius: 1,
                                wordBreak: "break-all",
                              }}
                            >
                              {selectedAttribute._id}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontWeight: 500, mb: 0.5 }}
                            >
                              Name
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ color: "#333", fontWeight: 600 }}
                            >
                              {selectedAttribute.name}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontWeight: 500, mb: 0.5 }}
                            >
                              Type
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {getTypeIcon(selectedAttribute.type)}
                              <Chip
                                label={
                                  selectedAttribute.type
                                    .charAt(0)
                                    .toUpperCase() +
                                  selectedAttribute.type.slice(1)
                                }
                                size="medium"
                                sx={{
                                  backgroundColor: getTypeBgColor(
                                    selectedAttribute.type,
                                  ),
                                  color: getTypeColor(selectedAttribute.type),
                                  borderColor: getTypeColor(
                                    selectedAttribute.type,
                                  ),
                                  fontWeight: 600,
                                  fontSize: "14px",
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontWeight: 500, mb: 0.5 }}
                            >
                              Category
                            </Typography>
                            <Chip
                              label={selectedAttribute.category || "General"}
                              size="medium"
                              variant="outlined"
                              sx={{
                                borderColor: "#1976d2",
                                color: "#1976d2",
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Values Card */}
                  <Card
                    sx={{
                      mb: 3,
                      borderRadius: 3,
                      border: "1px solid #e6e6e6",
                      backgroundColor: "#fff",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 3, color: "#333", fontWeight: 600 }}
                      >
                        Attribute Values
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        {(
                          selectedAttribute.values ||
                          selectedAttribute.options ||
                          []
                        ).length > 0 ? (
                          (
                            selectedAttribute.values ||
                            selectedAttribute.options ||
                            []
                          ).map((value, index) => (
                            <Chip
                              key={index}
                              label={value}
                              size="medium"
                              variant="outlined"
                              sx={{
                                backgroundColor: "#f8f9fa",
                                borderColor: "#dee2e6",
                                color: "#495057",
                                fontWeight: 500,
                                "&:hover": {
                                  backgroundColor: "#e9ecef",
                                  borderColor: "#adb5bd",
                                },
                              }}
                            />
                          ))
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: "#999", fontStyle: "italic" }}
                          >
                            No values defined
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Status & Settings Card */}
                  <Card
                    sx={{
                      mb: 3,
                      borderRadius: 3,
                      border: "1px solid #e6e6e6",
                      backgroundColor: "#fff",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 3, color: "#333", fontWeight: 600 }}
                      >
                        Status & Settings
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontWeight: 500, mb: 1 }}
                            >
                              Required
                            </Typography>
                            <Chip
                              label={
                                selectedAttribute.required
                                  ? "Required"
                                  : "Optional"
                              }
                              size="medium"
                              color={
                                selectedAttribute.required
                                  ? "success"
                                  : "default"
                              }
                              sx={{
                                backgroundColor: selectedAttribute.required
                                  ? "#e8f5e8"
                                  : "#f5f5f5",
                                borderColor: selectedAttribute.required
                                  ? "#4caf50"
                                  : "#ccc",
                                color: selectedAttribute.required
                                  ? "#4caf50"
                                  : "#666",
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontWeight: 500, mb: 1 }}
                            >
                              Status
                            </Typography>
                            <Chip
                              label={
                                selectedAttribute.active ? "Active" : "Inactive"
                              }
                              size="medium"
                              color={
                                selectedAttribute.active ? "primary" : "default"
                              }
                              sx={{
                                backgroundColor: selectedAttribute.active
                                  ? "#e3f2fd"
                                  : "#f5f5f5",
                                borderColor: selectedAttribute.active
                                  ? "#1976d2"
                                  : "#ccc",
                                color: selectedAttribute.active
                                  ? "#1976d2"
                                  : "#666",
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontWeight: 500, mb: 1 }}
                            >
                              Created At
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#333" }}>
                              {new Date(
                                selectedAttribute.createdAt,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Description Card */}
                  {selectedAttribute.description && (
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: "1px solid #e6e6e6",
                        backgroundColor: "#fff",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, color: "#333", fontWeight: 600 }}
                        >
                          Description
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#555", lineHeight: 1.6 }}
                        >
                          {selectedAttribute.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, bgcolor: "#f8f9fa" }}>
              <Button
                onClick={() => setDetailsDialogOpen(false)}
                variant="contained"
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this attribute? This action
                cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={confirmDelete} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

export default ProductAttributes;
